import { BUSINESS_ACCOUNT_FETCH_ERRORS } from 'businessTraveller/api/requestGetAccountBusiness'
import { isProduction } from 'lib/env/isProduction'

/**
 * Cookie for cached business/employee attributes used by Optimizely.
 * Populated from /api/business-traveller/me once per login, cleared on logout.
 */
export const BUSINESS_OPTIMIZELY_ATTRIBUTES_COOKIE = 'lebt_optimizely_business'
/** Sentinel value: API was called and user is not a business user — skip fetch on subsequent requests */
export const BUSINESS_OPTIMIZELY_NONE_SENTINEL = 'none'

export interface BusinessOptimizelyAttributes {
  business_id: string
  employee_id: string
  /** Optional; omit when absent so Optimizely treats as absent, not empty string */
  customer_id?: string
  /** Email domain only (e.g. acme.com), not full email to avoid PII */
  employee_email_domain: string
}

const COOKIE_MAX_AGE_MS = 24 * 60 * 60 * 1000 // 24 hours (cleared on logout anyway)
/** Sentinel TTL: short so users newly added to a business get picked up within this window */
export const BUSINESS_OPTIMIZELY_NONE_SENTINEL_MAX_AGE_MS = 3 * 60 * 1000 // 3 minutes

/**
 * Parse cookie value. Express cookie-parser already decodes the value, so no decodeURIComponent.
 */
export function parseBusinessOptimizelyAttributes(cookieValue: string | undefined): BusinessOptimizelyAttributes | null {
  if (!cookieValue || cookieValue === BUSINESS_OPTIMIZELY_NONE_SENTINEL) return null
  try {
    const parsed = JSON.parse(cookieValue) as BusinessOptimizelyAttributes
    if (
      typeof parsed?.business_id === 'string' &&
      typeof parsed?.employee_id === 'string' &&
      typeof parsed?.employee_email_domain === 'string'
    ) {
      return parsed
    }
    return null
  } catch {
    return null
  }
}

export function serializeBusinessOptimizelyAttributes(attrs: BusinessOptimizelyAttributes): string {
  return JSON.stringify(attrs)
}

export function getBusinessOptimizelyAttributesCookieOptions(maxAge: number = COOKIE_MAX_AGE_MS) {
  return {
    maxAge,
    path: '/',
    httpOnly: false,
    secure: isProduction(),
  }
}

/**
 * Clear the business Optimizely attributes cookie (client-side).
 * Call on logout and login to ensure fresh fetch for the new session.
 */
export function clearBusinessOptimizelyAttributesCookie() {
  document.cookie = `${BUSINESS_OPTIMIZELY_ATTRIBUTES_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/`
}

/** Errors from requestGetAccountBusiness that mean "not a business user" — set sentinel. Transient errors (OTHER, network) return false. */
export const NOT_BUSINESS_USER_ERRORS = new Set<string>([
  BUSINESS_ACCOUNT_FETCH_ERRORS.NO_BUSINESS_ASSOCIATED,
  BUSINESS_ACCOUNT_FETCH_ERRORS.INVITE_PENDING,
  BUSINESS_ACCOUNT_FETCH_ERRORS.EMPLOYEE_DEACTIVATED,
  BUSINESS_ACCOUNT_FETCH_ERRORS.EMPLOYEE_NOT_ACTIVATED,
  // Intentionally exclude LE_ACCOUNT_NOT_LINKED_TO_BUSINESS:
  // this can be transient during onboarding, so we avoid sentinel caching and retry soon.
])

export function isNotBusinessUserError(err: unknown): boolean {
  return typeof err === 'string' && NOT_BUSINESS_USER_ERRORS.has(err)
}
